import type { ExecStreamEndReason, ExecStreamHandle, ExecStreamStatus } from "./client";

/** Producer-side surface for an `ExecStreamHandle` adapter — mirrors
 * `LogStreamEmitter` in logStream.ts. Only the subscribe/send/close half
 * (`ExecStreamHandle`) is visible to callers like the terminal component. */
interface ExecStreamEmitter extends ExecStreamHandle {
  emitOutput(chunk: Uint8Array): void;
  emitStatus(status: ExecStreamStatus): void;
  /** Also tears the stream down — same one-shot-then-dead contract as
   * `LogStreamEmitter.emitEnd`. */
  emitEnd(reason: ExecStreamEndReason): void;
}

const textEncoder = new TextEncoder();

/**
 * Minimal pub-sub shared by both `ExecStreamHandle` adapters (the future
 * real WS transport and `mockClient.ts`'s synthetic shell), same shape as
 * `createLogStreamEmitter`. `onInput`/`onResize` are the transport's own
 * hooks for what to do with a write — passed in rather than exposed on the
 * handle, since callers only ever see the `ExecStreamHandle` half.
 */
export function createExecStreamEmitter(
  teardown: () => void,
  onInput: (data: Uint8Array) => void,
  onResize: (cols: number, rows: number) => void,
): ExecStreamEmitter {
  const outputHandlers = new Set<(chunk: Uint8Array) => void>();
  const statusHandlers = new Set<(status: ExecStreamStatus) => void>();
  const endHandlers = new Set<(reason: ExecStreamEndReason) => void>();
  let closed = false;

  const handle: ExecStreamEmitter = {
    onOutput(handler) {
      outputHandlers.add(handler);
      return () => outputHandlers.delete(handler);
    },
    onStatus(handler) {
      statusHandlers.add(handler);
      return () => statusHandlers.delete(handler);
    },
    onEnd(handler) {
      endHandlers.add(handler);
      return () => endHandlers.delete(handler);
    },
    sendInput(data) {
      if (closed) return;
      onInput(typeof data === "string" ? textEncoder.encode(data) : data);
    },
    sendResize(cols, rows) {
      if (closed) return;
      onResize(cols, rows);
    },
    close() {
      if (closed) return;
      closed = true;
      outputHandlers.clear();
      statusHandlers.clear();
      endHandlers.clear();
      teardown();
    },
    emitOutput(chunk) {
      if (closed) return;
      for (const handler of outputHandlers) handler(chunk);
    },
    emitStatus(status) {
      if (closed) return;
      for (const handler of statusHandlers) handler(status);
    },
    emitEnd(reason) {
      if (closed) return;
      for (const handler of endHandlers) handler(reason);
      handle.close();
    },
  };

  return handle;
}
